import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  COULEURS_AVATAR,
  LIBELLES_COULEURS,
  LIBELLES_SILHOUETTES,
  SILHOUETTES_AVATAR,
  type CouleurAvatar,
  type SilhouetteAvatar,
} from '@repere/core';
import { useRef, useState } from 'react';
import { messageDErreur, useSession } from '../auth/session';
import { Alerte, Bouton, Invisible } from '../ui';
import { Avatar, type AvatarEnfant } from './Avatar';

/**
 * Choix de l'avatar d'un enfant : une photo, ou une silhouette et une couleur.
 *
 * L'ordre des options n'est pas neutre. La silhouette vient **avant** la photo : elle
 * fonctionne tout de suite, sans rien téléverser, et un parent qui n'a pas envie de
 * déposer le portrait de son enfant dans une application ne doit pas avoir l'impression
 * de renoncer à quelque chose.
 *
 * La photo est annoncée pour ce qu'elle est : chiffrée avec la clé du dossier, visible des
 * deux parents et de personne d'autre. Le dire ici plutôt que dans des conditions
 * générales est ce qui permet de décider en connaissance de cause — c'est la donnée la
 * plus sensible que le produit détienne.
 */
export function ChoixAvatar({
  childId,
  prenom,
  naissance,
  avatar,
  maintenant,
}: {
  childId: string;
  prenom: string;
  naissance: string;
  avatar: AvatarEnfant;
  maintenant: Date;
}) {
  const { client } = useSession();
  const queryClient = useQueryClient();
  const fichier = useRef<HTMLInputElement>(null);
  const [erreurPhoto, setErreurPhoto] = useState<string | null>(null);

  const rafraichir = () => {
    void queryClient.invalidateQueries({ queryKey: ['case-file'] });
    void queryClient.invalidateQueries({ queryKey: ['case-files'] });
  };

  const definir = useMutation({
    mutationFn: (input: { silhouette: SilhouetteAvatar; color: CouleurAvatar }) =>
      client.request(`/children/${childId}/avatar`, { method: 'PUT', body: input }),
    onSuccess: rafraichir,
  });

  const deposer = useMutation({
    mutationFn: (image: File) => {
      const formulaire = new FormData();
      formulaire.append('file', image);
      return client.request(`/children/${childId}/avatar/photo`, {
        method: 'POST',
        form: formulaire,
      });
    },
    onError: (cause) => setErreurPhoto(messageDErreur(cause)),
    onSuccess: () => {
      setErreurPhoto(null);
      rafraichir();
    },
  });

  const retirer = useMutation({
    mutationFn: () =>
      client.request(`/children/${childId}/avatar/photo`, { method: 'DELETE' }),
    onSuccess: rafraichir,
  });

  return (
    <section className="choix-avatar" aria-labelledby={`avatar-${childId}`}>
      <h3 id={`avatar-${childId}`}>Son avatar</h3>

      <div className="choix-avatar-apercu">
        <Avatar
          childId={childId}
          avatar={avatar}
          prenom={prenom}
          naissance={naissance}
          taille={96}
          maintenant={maintenant}
        />
        <p className="discret">
          L'âge s'y met à jour tout seul — il est calculé à partir de la date de naissance,
          jamais enregistré.
        </p>
      </div>

      <fieldset className="choix-avatar-groupe">
        <legend>Silhouette</legend>
        <div className="choix-avatar-options">
          {SILHOUETTES_AVATAR.map((silhouette) => (
            <button
              key={silhouette}
              type="button"
              className={silhouette === avatar.silhouette ? 'actif' : undefined}
              aria-pressed={silhouette === avatar.silhouette}
              onClick={() => definir.mutate({ silhouette, color: avatar.color })}
            >
              <Avatar
                childId={childId}
                avatar={{ ...avatar, silhouette, hasPhoto: false }}
                prenom={prenom}
                naissance={naissance}
                taille={40}
                maintenant={maintenant}
              />
              {LIBELLES_SILHOUETTES[silhouette]}
            </button>
          ))}
        </div>
        <p className="aide">
          Repère n'enregistre pas le genre d'un enfant et n'a pas à le demander : « neutre »
          convient à tous. Les deux autres sont là si votre enfant s'y reconnaît mieux.
        </p>
      </fieldset>

      <fieldset className="choix-avatar-groupe">
        <legend>Couleur</legend>
        <div className="choix-avatar-couleurs">
          {COULEURS_AVATAR.map((couleur) => (
            <button
              key={couleur}
              type="button"
              className={`pastille-couleur avatar-${couleur}${couleur === avatar.color ? ' actif' : ''}`}
              aria-pressed={couleur === avatar.color}
              onClick={() => definir.mutate({ silhouette: avatar.silhouette, color: couleur })}
            >
              <Invisible>{LIBELLES_COULEURS[couleur]}</Invisible>
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="choix-avatar-groupe">
        <legend>Photo</legend>
        <p className="aide">
          Elle est chiffrée avec la clé du dossier avant d'être enregistrée : les deux
          parents la voient, personne d'autre. JPEG, PNG, WebP ou HEIC, 2 Mo au maximum.
        </p>

        {erreurPhoto ? (
          <Alerte titre="La photo n’a pas pu être enregistrée">{erreurPhoto}</Alerte>
        ) : null}

        <input
          ref={fichier}
          id={`avatar-photo-${childId}`}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          onChange={(evenement) => {
            const image = evenement.target.files?.[0];
            if (image) deposer.mutate(image);
            // Le champ est vidé pour que redéposer le **même** fichier redéclenche
            // l'événement : sans cela, corriger un envoi raté demanderait de choisir une
            // autre image.
            evenement.target.value = '';
          }}
        />
        <label htmlFor={`avatar-photo-${childId}`} className="bouton secondaire">
          {deposer.isPending
            ? 'Envoi en cours…'
            : avatar.hasPhoto
              ? 'Remplacer la photo'
              : 'Choisir une photo'}
        </label>

        {avatar.hasPhoto ? (
          <Bouton
            variante="lien"
            onClick={() => retirer.mutate()}
            disabled={retirer.isPending}
          >
            Retirer la photo
          </Bouton>
        ) : null}
      </fieldset>
    </section>
  );
}
